import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation, Routes, Route } from 'react-router-dom';
import { ThemeProvider, CssBaseline, useMediaQuery, useTheme } from "@mui/material";
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Theme from './components/Theme';
import Login from './pages/Auth/Login';
import Forgot from './pages/Auth/Forgot';
import Otp from './pages/Auth/Otp';
import Confirm from './pages/Auth/Confirm';
import Sidebar from './components/Sidebar';
import PrivateRoute from './routes/PrivateRoute';
import routeData from './routes/routeData'; 
import Unauthorized from './pages/Unauthorized';
import Footer from './components/Footer';

// Create Snackbar Context
const SnackbarContext = createContext();

export const useSnackbar = () => useContext(SnackbarContext);

function App() {
  const theme = useTheme();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const toastPosition = isMobile ? { vertical: 'top', horizontal: 'center' } : { vertical: 'top', horizontal: 'right' };

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info', // 'success', 'error', 'warning', 'info'
  });

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar({ ...snackbar, open: false });
  };

  const noSidebarRoutes = ['/', '/forgot', '/otp', '/confirm'];
  const hideSidebar = noSidebarRoutes.includes(location.pathname);

  // Define background colors based on severity
  const getBackgroundColor = (severity) => {
    switch (severity) {
      case 'success':
        return 'rgba(46, 125, 50, 0.9)'; // Green for success
      case 'error':
        return 'rgba(211, 47, 47, 0.9)'; // Red for failed
      case 'warning':
        return 'rgba(237, 108, 2, 0.9)'; // Orange for delete (warning)
      default:
        return 'rgba(2, 136, 209, 0.9)'; // Blue for info
    }
  };

  return (
    <ThemeProvider theme={Theme}>
      <CssBaseline />
      <SnackbarContext.Provider value={{ showSnackbar }}>
        {/* Global styles */}
        <style>
          {`
            body {
              margin: 0;
              padding: 0;
              font-family: 'Poppins', sans-serif;
              background-color: #f4f6f8;
            }
          `}
        </style>

        {/* Conditional rendering based on route */}          
        {hideSidebar ? (
          <Routes>
            <Route path="/" element={<Login />} />
            <Route path="/forgot" element={<Forgot />} />
            <Route path="/otp" element={<Otp />} />
            <Route path="/confirm" element={<Confirm />} />
          </Routes>
        ) : (
          <>
            <Sidebar>
              <Routes>
                {routeData.map(({ path, element, roles }) => (
                  <Route
                    key={path}
                    path={path}
                    element={
                      <PrivateRoute allowedRoles={roles}>
                        {element}
                      </PrivateRoute>
                    }
                  />
                ))}
                <Route path="/unauthorized" element={<Unauthorized />} />
              </Routes>
            </Sidebar>
              {!isMobile && <Footer />}
          </>
        )}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={handleClose}
          anchorOrigin={toastPosition}
          sx={{
            '& .MuiPaper-root': {
              backgroundColor: getBackgroundColor(snackbar.severity),
              color: '#fff', // White text for contrast
              borderRadius: '8px', // Match app's rounded corners
            }
          }}
        >
          <Alert
            onClose={handleClose}
            severity={snackbar.severity}
            sx={{
              width: '100%',
              backgroundColor: 'transparent', // Let Snackbar's background dominate
              color: 'inherit', // Inherit text color from Snackbar
              '& .MuiAlert-icon': {
                color: 'inherit', // Ensure icon matches text color
              }
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </SnackbarContext.Provider>
    </ThemeProvider>
  );
}

export default App;